/**
 * Tiny home-made Canvas line chart (SPEC §13: "graphiques légers, Canvas maison
 * ou uPlot, pas de bibliothèque lourde"). Draws one or more lap-time series
 * where LOWER is better, so the y-axis is inverted (going up = faster).
 */

export interface ChartSeries {
  label: string;
  color: string;
  points: { x: number; y: number }[];
}

const PAD_LEFT = 52;
const PAD_RIGHT = 12;
const PAD_TOP = 14;
const PAD_BOTTOM = 26;

export function drawLapChart(
  canvas: HTMLCanvasElement,
  series: ChartSeries[],
  formatY: (ms: number) => string,
  options?: { invertY?: boolean },
): void {
  // Lap times: lower is better → y-axis inverted (default). Points: higher is
  // better → set invertY:false so the line climbs with a good season.
  const invertY = options?.invertY ?? true;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const cssW = canvas.clientWidth || 480;
  const cssH = canvas.clientHeight || 220;
  canvas.width = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);

  const all = series.flatMap((s) => s.points);
  if (all.length === 0) return;

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of all) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }
  if (maxX === minX) maxX = minX + 1;
  // 4 % headroom so the extreme points aren't glued to the frame.
  const spanY = Math.max(maxY - minY, 1);
  minY -= spanY * 0.06;
  maxY += spanY * 0.06;

  const plotW = cssW - PAD_LEFT - PAD_RIGHT;
  const plotH = cssH - PAD_TOP - PAD_BOTTOM;
  const px = (x: number): number => PAD_LEFT + ((x - minX) / (maxX - minX)) * plotW;
  // Inverted (lap times): smaller = higher on screen. Non-inverted (points):
  // larger = higher on screen.
  const py = (y: number): number =>
    invertY
      ? PAD_TOP + ((y - minY) / (maxY - minY)) * plotH
      : PAD_TOP + ((maxY - y) / (maxY - minY)) * plotH;

  const gridColor = 'rgba(147, 161, 177, 0.18)';
  const textColor = '#93a1b1';
  ctx.font = '11px system-ui, sans-serif';
  ctx.textBaseline = 'middle';

  // Horizontal gridlines + y labels (4 ticks).
  ctx.strokeStyle = gridColor;
  ctx.fillStyle = textColor;
  ctx.lineWidth = 1;
  for (let i = 0; i <= 4; i++) {
    const yVal = minY + ((maxY - minY) * i) / 4;
    const y = py(yVal);
    ctx.beginPath();
    ctx.moveTo(PAD_LEFT, y);
    ctx.lineTo(cssW - PAD_RIGHT, y);
    ctx.stroke();
    ctx.textAlign = 'right';
    ctx.fillText(formatY(yVal), PAD_LEFT - 6, y);
  }

  // Series.
  for (const s of series) {
    if (s.points.length === 0) continue;
    ctx.strokeStyle = s.color;
    ctx.fillStyle = s.color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    s.points.forEach((p, i) => {
      const x = px(p.x);
      const y = py(p.y);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
    for (const p of s.points) {
      ctx.beginPath();
      ctx.arc(px(p.x), py(p.y), 3, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}
